import React from 'react';
import { Card, CardContent, CardDescription, CardHeader } from './ui/card';
import { Check, Phone, MessageSquare } from 'lucide-react';
import PaymentButton from './PaymentButton';

const Pricing = () => {
  const plans = [
    {
      name: 'Standard',
      monthlyPrice: 45,
      planId: 'standard',
      callsPerMonth: 100,
      chatsPerMonth: 500,
      description: 'For small businesses with light call and chat volume.',
      features: [
        'Dedicated AI phone number',
        '24/7 inbound call answering',
        'AI chat widget for your website',
        '500 chat conversations / month',
        'Call recordings, transcripts & summaries',
        'Lead capture with built-in CRM',
        'Spam & telemarketing filter',
        'Multilingual support (10+ languages)',
        'SMS & email notifications',
        'FAQ-trained AI responses',
        'Post-call & post-chat actions',
      ],
      popular: false,
    },
    {
      name: 'Growth',
      monthlyPrice: 145,
      planId: 'growth',
      callsPerMonth: 300,
      chatsPerMonth: 2000,
      description: 'For growing teams that want the full Miadesk automation suite.',
      features: [
        'Everything in Standard, plus:',
        '2,000 chat conversations / month',
        'AI lead qualification on calls & chat',
        'Outbound AI campaign builder (calls, SMS & email)',
        'Multi-step sales sequences',
        'AI-powered ticketing system',
        'Custom chat widget branding & colors',
        'Chat-to-human escalation & live takeover',
        'Professional invoicing with PDF',
        'Conversation insights & advanced analytics',
        'Custom greetings, voice & widget profile',
        'Team management & roles',
        'Priority support',
      ],
      popular: true,
    },
  ];

  return (
    <section id="pricing" className="py-20 px-6" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <p className="text-purple-400 text-sm font-semibold tracking-widest uppercase mb-3">Pricing</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-base text-gray-400 max-w-2xl mx-auto">
            Every plan includes a dedicated AI phone number and chat widget. Start saving time with just one scheduled appointment.
          </p>
        </div>

        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center gap-4 px-5 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <span className="flex items-center gap-2 text-gray-300 text-sm">
              <Phone className="w-4 h-4 text-purple-400" />
              <span className="font-semibold text-white">AI phone number</span>
            </span>
            <span className="text-gray-600">+</span>
            <span className="flex items-center gap-2 text-gray-300 text-sm">
              <MessageSquare className="w-4 h-4 text-blue-400" />
              <span className="font-semibold text-white">AI chat widget</span>
            </span>
            <span className="text-gray-500 text-xs">included in all plans</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <Card
              key={plan.name}
              data-testid={`pricing-card-${plan.planId}`}
              className={`relative bg-gradient-to-br ${
                plan.popular
                  ? 'from-purple-900/40 to-blue-900/40 border-purple-500/50'
                  : 'from-gray-900/40 to-gray-800/40 border-white/10'
              } backdrop-blur-sm`}
            >
              {plan.popular && (
                <div className="absolute -top-4 right-6">
                  <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                </div>
              )}

              <CardHeader className="pb-4">
                <CardDescription className={`text-sm font-medium ${plan.popular ? 'text-purple-300' : 'text-gray-400'}`}>
                  {plan.name}
                </CardDescription>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-5xl font-bold text-white" data-testid={`price-${plan.planId}`}>
                    ${plan.monthlyPrice}
                  </span>
                  <span className="text-lg text-gray-400">/ month</span>
                </div>
                <div className="text-sm font-medium space-y-0.5">
                  <p className="text-purple-300">{plan.callsPerMonth} calls / month</p>
                  <p className="text-blue-300">{plan.chatsPerMonth} chats / month</p>
                </div>
                <CardDescription className="text-gray-400 text-sm leading-relaxed mt-2">
                  {plan.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-5">
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {plan.planId === 'standard' ? 'Features included:' : 'Everything in Standard, plus:'}
                  </p>
                  {plan.features.filter((f) => !f.startsWith('Everything in')).map((feature, index) => (
                    <div key={index} className="flex items-start gap-2.5">
                      <div className="mt-0.5 flex-shrink-0">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center ${plan.popular ? 'bg-purple-500/20' : 'bg-teal-500/20'}`}>
                          <Check className={`w-2.5 h-2.5 ${plan.popular ? 'text-purple-400' : 'text-teal-400'}`} />
                        </div>
                      </div>
                      <p className="text-gray-200 text-sm leading-relaxed">{feature}</p>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 pt-2">
                  <PaymentButton
                    planId={plan.planId}
                    planName={plan.name}
                    billingPeriod="monthly"
                    buttonText="Start free today"
                    className={`w-full py-5 text-sm font-semibold rounded-xl ${
                      plan.popular
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg shadow-purple-500/25'
                        : 'bg-transparent border-2 border-white/20 text-white hover:bg-white/10'
                    } transition-all`}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-16 text-center space-y-6">
          <div className="inline-flex flex-col items-center p-6 rounded-2xl bg-gradient-to-r from-gray-900/40 to-gray-800/40 border border-white/10">
            <h3 className="text-xl font-bold text-white mb-1">Enterprise</h3>
            <p className="text-gray-400 text-sm mb-4">For large organizations with multi-location operations</p>
            <ul className="text-left text-sm text-gray-300 space-y-1.5 mb-4">
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-teal-400" /> Custom integrations (CRM, ERP, internal tools)</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-teal-400" /> Dedicated account manager and onboarding</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-teal-400" /> Multi-location and department-based routing</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-teal-400" /> White-label option available</li>
            </ul>
            <a href="#contact" className="text-purple-400 hover:text-purple-300 font-medium text-sm underline underline-offset-4">Talk to Sales</a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
